import * as autobind from 'autobind';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { Button, Container, Modal, Header, Grid } from 'semantic-ui-react';
import { Query, OrderDefinition, ItemReference } from 'stores/dataStore';
import { TableView, TableModel } from 'widgets/collections/table';
import { connect } from 'redux-scaffolding-ts';
import { isNullOrWhiteSpaces, extractFriendlyIdNumber } from 'utils/useful-functions';
import { nameof } from 'utils/object';
import MRClusterEditor from 'widgets/bussiness/mrcluster-editor';
import EquipmentTypeEditor from 'widgets/bussiness/equipment-type-editor';
import OemEditor from 'widgets/bussiness/oem-editor';
import MachineModelEditor from 'widgets/bussiness/machine-model-editor';
import { ClearableTimerInput } from 'widgets/editors/clearable-timer-input';
import { TheoreticalTemplateItemDto, TheoreticalTemplatesStore } from 'stores/assessments/templates/theoretical-templates-store';

interface ChooseTheoreticalTemplatesViewProps extends WithTranslation {
  onAddTemplate?: (template: TheoreticalTemplateItemDto) => void;
  onCloseTemplateModal?: () => void;
  alreadySelectedTemplateId?: string;
  theoreticalTemplatesStore?: TheoreticalTemplatesStore;
  professionId?: string;
  profileId?: string;
  typeRelated?: 'TNA' | 'Event';
}

interface ChooseTnaTemplatesViewState {
  query: Query;
  selectedTemplate: TheoreticalTemplateItemDto;
  templateTitleOrId: string;
  cluster: ItemReference;
  equipmentType: ItemReference;
  oem: ItemReference;
  machineModel: ItemReference;
}

@connect(['theoreticalTemplatesStore', TheoreticalTemplatesStore])
class ChooseTheoreticalTemplatesView extends React.Component<ChooseTheoreticalTemplatesViewProps, ChooseTnaTemplatesViewState> {
  constructor(props: ChooseTheoreticalTemplatesViewProps) {
    super(props);

    this.state = {
      query: {
        searchQuery: ``,
        orderBy: [],
        filter: [],
        skip: 0,
        take: 10
      },
      templateTitleOrId: undefined,
      cluster: null,
      equipmentType: null,
      oem: null,
      machineModel: null,
      selectedTemplate: null
    };
  }

  componentDidMount() {
    this.load();
  }

  @autobind
  private handleOnAddTemplate = () => {
    const { selectedTemplate } = this.state;
    if (selectedTemplate == null) return;

    this.props.onAddTemplate && this.props.onAddTemplate(selectedTemplate);
    this.props.onCloseTemplateModal();
  };

  @autobind
  private handleOnCancel = () => {
    this.props.onCloseTemplateModal();
  };

  @autobind
  private load() {
    const filter = this.buildFilters();
    this.setState(
      {
        query: { ...this.state.query, filter }
      },
      () => this.props.theoreticalTemplatesStore.getAllAsync(this.state.query)
    );
  }

  private buildFilters = () => {
    let filters = [];

    if (!isNullOrWhiteSpaces(this.props.alreadySelectedTemplateId)) {
      filters.push({ Id: { ne: { value: this.props.alreadySelectedTemplateId, type: 'guid' } } });
    }

    if (!isNullOrWhiteSpaces(this.props.professionId)) {
      filters.push({ professionItemId: { eq: { type: 'guid', value: this.props.professionId } } });
    }

    if (!isNullOrWhiteSpaces(this.props.profileId)) {
      filters.push({ profileId: { eq: { type: 'guid', value: this.props.profileId } } });
    }

    if (!isNullOrWhiteSpaces(this.props.typeRelated)) {
      filters.push({ typeRelated: this.props.typeRelated });
    }

    if (!isNullOrWhiteSpaces(this.state.templateTitleOrId)) {
      const input = this.state.templateTitleOrId;
      const parts = [`contains(tolower(title), '${input.toLowerCase()}')`];

      const friendlyId = extractFriendlyIdNumber(input, 'TTh');
      if (!Number.isNaN(friendlyId)) {
        if (input.startsWith('TTh')) {
          parts.push(`cast(FriendlyId, 'Edm.String') eq '${friendlyId}'`);
        } else {
          parts.push(`contains(cast(FriendlyId, 'Edm.String'), '${friendlyId !== 0 ? friendlyId : input}')`);
        }
      }

      const f = `(${parts.join(' or ')})`;
      filters.push(f);
    }

    if (!isNullOrWhiteSpaces(this.state.cluster?.id)) {
      filters.push({ theoreticalTemplateMRDetails: { clusterId: { eq: { type: 'guid', value: this.state.cluster.id } } } });
    }

    if (!isNullOrWhiteSpaces(this.state.equipmentType?.id)) {
      filters.push({ theoreticalTemplateMRDetails: { equipmentTypeId: { eq: { type: 'guid', value: this.state.equipmentType.id } } } });
    }

    if (!isNullOrWhiteSpaces(this.state.oem?.id)) {
      filters.push({ theoreticalTemplateMRDetails: { oemId: { eq: { type: 'guid', value: this.state.oem.id } } } });
    }

    if (!isNullOrWhiteSpaces(this.state.machineModel?.id)) {
      filters.push({ theoreticalTemplateMRDetails: { machineModelId: { eq: { type: 'guid', value: this.state.machineModel.id } } } });
    }

    return filters;
  };

  @autobind
  private handleOrderBy(orderBy: OrderDefinition[]) {
    this.setState({ query: Object.assign(this.state.query, { orderBy }) }, this.load);
  }

  @autobind
  private handlePageChange(skip: number, take: number) {
    this.setState({ query: Object.assign(this.state.query, { skip, take }) }, this.load);
  }

  private handleFilterByTemplateTitleOrId(value: string) {
    this.setState(
      {
        templateTitleOrId: value
      },
      this.load
    );
  }

  private onClusterEditorChange = (value: ItemReference) => {
    this.setState(
      {
        cluster: value,
        equipmentType: null,
        oem: null,
        machineModel: null
      },
      this.load
    );
  };

  private onEquipmentTypeFilterChange = (value: ItemReference) => {
    this.setState(
      {
        equipmentType: value,
        oem: null,
        machineModel: null
      },
      this.load
    );
  };

  private onOemFilterChange = (value: ItemReference) => {
    this.setState(
      {
        oem: value,
        machineModel: null
      },
      this.load
    );
  };

  private onChangeMachineModelFilter = (value: ItemReference) => {
    this.setState(
      {
        machineModel: value
      },
      this.load
    );
  };

  private handleOnSelectedTemplate = (items: unknown[]) => {
    if (items && items.length > 0) this.setState({ selectedTemplate: items[0] as any });
  };

  public render() {
    const { t } = this.props as any;
    const { selectedTemplate } = this.state;

    const tableModel = {
      columns: [
        {
          title: t('Template ID'),
          tooltipRenderer: true,
          renderer: data => data.friendlyId,
          sortDefinition: {
            field: nameof<TheoreticalTemplateItemDto>('friendlyId'),
            useProfile: false
          },
          selectableHeader: true
        },
        {
          title: t('Template'),
          tooltipRenderer: true,
          renderer: data => data.title
        },
        {
          title: t('Cluster'),
          tooltipRenderer: true,
          renderer: data => data.theoreticalTemplateMRDetails?.cluster?.name
        },
        {
          title: t('Equipment Type'),
          tooltipRenderer: true,
          renderer: data => data.theoreticalTemplateMRDetails?.equipmentType?.name
        },
        {
          title: t('OEM'),
          tooltipRenderer: true,
          renderer: data => data.theoreticalTemplateMRDetails?.oem?.name
        },
        {
          title: t('Machine Model'),
          tooltipRenderer: true,
          renderer: data => data.theoreticalTemplateMRDetails?.machineModel?.name
        }
      ],
      data: this.props.theoreticalTemplatesStore.state
    } as TableModel<TheoreticalTemplateItemDto>;

    return (
      <Modal
        size="large"
        className="template-search__modal"
        open
        closeOnEscape={true}
        onClose={this.handleOnCancel}
        closeOnDimmerClick={false}
      >
        <Modal.Header className="borderless-header">
          <Header as="h2" className="modal-header-title">
            {t('Select Template')}
          </Header>
        </Modal.Header>
        <Modal.Content className="modal-content">
          <Grid className="choose-template-search__first-row__column-filters">
            <Grid.Row columns="5">
              <Grid.Column width="4">
                <ClearableTimerInput
                  icon="search"
                  placeholder={t('Search in Template or ID')}
                  onChange={(event, data) => this.handleFilterByTemplateTitleOrId(data.value)}
                />
              </Grid.Column>

              <Grid.Column width="3">
                <MRClusterEditor
                  clearable={true}
                  nullable
                  value={this.state.cluster}
                  onChange={this.onClusterEditorChange}
                  placeholder={t('Cluster')}
                />
              </Grid.Column>

              <Grid.Column width="3">
                <EquipmentTypeEditor
                  nullable
                  clearable={true}
                  clusterId={this.state.cluster ? this.state.cluster.id : ''}
                  readOnly={!(this.state.cluster && this.state.cluster.id)}
                  value={this.state.equipmentType}
                  onChange={this.onEquipmentTypeFilterChange}
                  placeholder={t('Equipment Type')}
                />
              </Grid.Column>

              <Grid.Column width="3">
                <OemEditor
                  nullable
                  clearable={true}
                  value={this.state.oem}
                  readonly={!(this.state.equipmentType && this.state.equipmentType.id)}
                  onChange={this.onOemFilterChange}
                  placeholder={t('OEM')}
                  equipmentId={this.state.equipmentType != null ? this.state.equipmentType.id : undefined}
                />
              </Grid.Column>

              <Grid.Column width="3">
                <MachineModelEditor
                  cascade="true"
                  nullable
                  clearable={true}
                  oemId={this.state.oem ? this.state.oem.id : ''}
                  equipmentId={this.state.equipmentType ? this.state.equipmentType.id : ''}
                  value={this.state.machineModel}
                  onChange={data => this.onChangeMachineModelFilter(data)}
                  placeholder={t('Machine Model')}
                  readonly={!(this.state.oem && this.state.oem.id)}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>

          <Container className="template-list-table">
            <TableView
              selectable={true}
              maxSelection={1}
              model={tableModel}
              onOrderByChanged={this.handleOrderBy}
              onRefresh={this.load}
              canEdit={false}
              canDelete={false}
              hiddeMenu={true}
              onSelectionChange={this.handleOnSelectedTemplate}
              onPageChange={this.handlePageChange}
              selectionType={'checkbox'}
              unselectFirstRow
            ></TableView>
          </Container>
        </Modal.Content>
        <Modal.Actions>
          <Button className="basic" onClick={this.handleOnCancel}>
            {t('Cancel')}
          </Button>
          <Button className="add-template-button" disabled={selectedTemplate == null} onClick={this.handleOnAddTemplate} positive>
            {t('Select Template')}
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

// Wire up the React component to the Redux store
export default withTranslation()(ChooseTheoreticalTemplatesView);
